import {
  Bundesland,
  EndpointType,
  GridHistoricalMetadataModel,
  StationHistoricalMetadataModel,
} from '@/api/out';
import { DatasetRouterParams } from '@/router/model';
import { ApiStore } from '../api-store';
import {
  DatasetMetadata,
  RasterDatasetMetadata,
  StationDatasetMetadata,
} from './dataset-model';
import DatasetStore from '@/stores/dataset/dataset-module';
import StationDatasetStore from '@/stores/dataset/station-dataset-module';
import RasterDatasetStore from '@/stores/dataset/raster-dataset-module';
import { BBOX } from '@/stores/dataset/raster-dataset-model';
import { Frequency } from '@/util/datetime';

async function _fetchFullDatasetMetadata({
  type,
  resourceId,
}: DatasetRouterParams): Promise<
  GridHistoricalMetadataModel | StationHistoricalMetadataModel
> {
  switch (type) {
    case EndpointType.Station:
      return (
        await ApiStore.dataApi.stationApi.historicalStationMetadataStationHistoricalResourceIdMetadataGet(
          resourceId
        )
      ).data;
    case EndpointType.Grid:
      return (
        await ApiStore.dataApi.gridApi.historicalGridMetadataGridHistoricalResourceIdMetadataGet(
          resourceId
        )
      ).data;
    case EndpointType.Timeseries:
      // add timeseries for typescript (cover all cases)
      return (
        await ApiStore.dataApi.gridApi.historicalGridMetadataGridHistoricalResourceIdMetadataGet(
          resourceId
        )
      ).data;
    // No default case, respect typescript signature, then this function covers all cases
  }
}

function _parseRasterDatasetMetadata(
  metadata: GridHistoricalMetadataModel
): RasterDatasetMetadata {
  return {
    bbox: new BBOX(
      metadata.bbox[0],
      metadata.bbox[1],
      metadata.bbox[2],
      metadata.bbox[3]
    ),
    bboxOuter: new BBOX(
      metadata.bbox_outer[0],
      metadata.bbox_outer[1],
      metadata.bbox_outer[2],
      metadata.bbox_outer[3]
    ),
    spatialResolution: metadata.spatial_resolution_m,
    crs: metadata.crs,
    gridBounds: new BBOX(
      metadata.grid_bounds[0],
      metadata.grid_bounds[1],
      metadata.grid_bounds[2],
      metadata.grid_bounds[3]
    ),
  };
}

function _parseStationDatasetMetadata(
  metadata: StationHistoricalMetadataModel
): StationDatasetMetadata {
  return {
    stationAltitudeRange: [0, 3800], // highest known station is 3450, 'Grossglockner' is 3798
    states: Object.values(Bundesland),
    idType: metadata.id_type,
  };
}

function _parseDatasetCommonMetadata(
  metadata: StationHistoricalMetadataModel | GridHistoricalMetadataModel,
  datasetParams: DatasetRouterParams
): DatasetMetadata {
  if (!metadata.start_time.includes('+')) {
    metadata.start_time += '+00:00';
  }
  if (!metadata.end_time.includes('+')) {
    metadata.end_time += '+00:00';
  }

  return {
    frequency: metadata.frequency as Frequency,
    title: metadata.title,
    parameters: metadata.parameters
      .filter((param) => param.name !== 'time')
      .map((param) => ({
        name: param.name,
        shortDesc: param.long_name,
        desc: param.desc,
        unit: param.unit,
      })),
    datasetParams,
    startDate: new Date(metadata.start_time),
    endDate: new Date(metadata.end_time),
  };
}

export async function loadStoresFromDatasetParams(
  params: DatasetRouterParams
): Promise<void> {
  DatasetStore.updateLoading(true);
  StationDatasetStore.updateLoading(true);
  RasterDatasetStore.updateLoading(true);

  const fullMetadata = await _fetchFullDatasetMetadata(params);

  const commonMetadata = _parseDatasetCommonMetadata(fullMetadata, params);
  DatasetStore.updateMetadata(commonMetadata);
  DatasetStore.updateLoading(false);

  if (params.type === EndpointType.Station) {
    const stationMetadata = _parseStationDatasetMetadata(
      <StationHistoricalMetadataModel>fullMetadata
    );
    StationDatasetStore.updateMetadata(stationMetadata);
  } else {
    const rasterMetadata = _parseRasterDatasetMetadata(
      <GridHistoricalMetadataModel>fullMetadata
    );
    RasterDatasetStore.updateMetadata(rasterMetadata);
  }

  StationDatasetStore.updateLoading(false);
  RasterDatasetStore.updateLoading(false);
}
